
import React from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { confirm } from "@/components/ui/confirm";
import { DataTable } from "@/components/ui/data-table";
import { Edit, Trash2, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Company } from '@/lib/types';
import { WebSearchStatus } from '@/services/companyWebSearchService';
import { deleteCompany } from '@/services/companyService';
import CompanyCard from './CompanyCard';

interface CompanyListProps {
  companies: Company[];
  isAdmin: boolean;
  webSearchStatuses: Record<string, WebSearchStatus>;
  isLoading: boolean;
}

const CompanyList: React.FC<CompanyListProps> = ({ 
  companies, 
  isAdmin, 
  webSearchStatuses,
  isLoading
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const handleDelete = async (companyId: string, companyName: string) => {
    confirm({
      title: "Delete Company",
      description: `Are you sure you want to delete ${companyName}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await deleteCompany(companyId);
          toast({
            title: "Success",
            description: `${companyName} has been deleted successfully.`,
          });
          // We'll handle the state update in the parent component
          // through a callback, but for now we'll refresh the page
          window.location.reload();
        } catch (error) {
          console.error("Error deleting company:", error);
          toast({
            title: "Error",
            description: "Failed to delete the company. Please try again.",
            variant: "destructive",
          });
        }
      },
    });
  };

  const handleCompanyClick = (companyId: string) => {
    navigate(`/companies/${companyId}`);
  };

  const renderWebSearchStatus = (companyId: string) => {
    const status = webSearchStatuses[companyId];
    
    if (!status) {
      return <span className="text-gray-400">Not started</span>;
    }
    
    switch (status) {
      case 'completed':
        return <span className="text-green-600 font-medium">Completed</span>;
      case 'pending':
        return <span className="text-amber-600 font-medium">Pending</span>;
      case 'processing':
        return <span className="text-blue-600 font-medium">Processing</span>;
      case 'failed':
        return <span className="text-red-600 font-medium">Failed</span>;
      default:
        return <span className="text-gray-400">Not started</span>;
    }
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex items-center">
          {row.original.website && (
            <a href={row.original.website} target="_blank" rel="noopener noreferrer" className="mr-2">
              <Globe className="h-4 w-4 text-blue-500" />
            </a>
          )}
          <button 
            onClick={() => handleCompanyClick(row.original.id)}
            className="text-blue-600 hover:underline font-medium"
          >
            {row.getValue("name")}
          </button>
        </div>
      ),
    },
    {
      accessorKey: "industry",
      header: "Industry",
    },
    {
      accessorKey: "size",
      header: "Size",
      cell: ({ row }) => {
        const company = row.original;
        return company.size ? <Badge variant="outline">{company.size}</Badge> : null;
      },
    },
    {
      accessorKey: "website",
      header: "Website",
      cell: ({ row }) => (
        row.original.website ? (
          <a href={row.original.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
            {row.original.website}
          </a>
        ) : null
      ),
    },
    {
      accessorKey: "web_search_status",
      header: "Web Search",
      cell: ({ row }) => (
        <div className="flex items-center">
          <Globe className="h-4 w-4 mr-2 text-blue-500" />
          {renderWebSearchStatus(row.original.id)}
        </div>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }) => format(new Date(row.original.created_at), "PPP"),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center">
          {isAdmin && (
            <>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(`/companies/${row.original.id}/edit`)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleDelete(row.original.id, row.original.name)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </>
          )}
          {!isAdmin && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(`/companies/${row.original.id}`)}
            >
              View
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <div className="text-center py-4">Loading companies...</div>;
  }

  return (
    <>
      {/* Desktop View - Table */}
      <div className="hidden lg:block">
        <DataTable columns={columns} data={companies} />
      </div>
      
      {/* Mobile/Tablet View - Cards */}
      <div className="lg:hidden grid gap-4 sm:grid-cols-2">
        {companies.length === 0 ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">
            No companies found.
          </div>
        ) : (
          companies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              isAdmin={isAdmin}
              webSearchStatus={webSearchStatuses[company.id]}
              onView={handleCompanyClick}
              onEdit={isAdmin ? (id) => navigate(`/companies/${id}/edit`) : undefined}
              onDelete={isAdmin ? handleDelete : undefined}
            />
          ))
        )}
      </div>
    </>
  );
};

export default CompanyList;
